// Libraries
import { round } from "lodash";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";

// Types
import {
	ICommissionDetail,
	IEmployeeCommission,
	ISalesPerformanceExportInput,
	TPossibleUpdatedItemCommissions,
} from "@/DataPages/types/salesOperations/salesPerformance/models/crossStoreReconciliation";
// Third Party
import uniqBy from "lodash/uniqBy";

// Types
import type { ISystemSettings } from "@Settings/types/Common/model";
import type { Nullable } from "@Common/interfaces/types";
import type { ISaleIdentifier, ISalesPerformance } from "@DataPages/types/salesOperations/salesPerformance/models/salesPerformance";
import type { IGroupInclusion } from "@DataPages/types/salesOperations/salesPerformance/crossStoreReconciliationUtils";

function getIdentifiers(salesPerformanceData: ISalesPerformance[] | undefined, locationId: string): ISaleIdentifier[] {
	if (!salesPerformanceData?.length) {
		return [];
	}
	const seen = new Set<string>();
	const identifiers = salesPerformanceData
		.filter((sale) => sale?.sourceLocationId === locationId)
		.flatMap(
			(sale) =>
				sale?.identifiers?.map((identifierItem) => ({
					...identifierItem,
					label: !!identifierItem.dealerCodeFS
						? `${identifierItem.oemName} (${identifierItem.dealerCodeFS})`
						: identifierItem.oemName,
				})) || []
		)
		.filter((identifierItem) => {
			if (!identifierItem?.oemName || identifierItem.locationId !== locationId) {
				return false;
			}
			const key = identifierItem.label;
			if (seen.has(key)) {
				return false;
			}
			seen.add(key);
			return true;
		});
	identifiers.sort((a, b) => {
		if (a.oemName === "Pre-Owned") return 1;
		if (b.oemName === "Pre-Owned") return -1;
		return a.oemName.localeCompare(b.oemName);
	});
	return identifiers;
}

function getGroupInclusions(systemSettings: Nullable<ISystemSettings>): IGroupInclusion[] | null {
	if (!systemSettings?.crossStoreEmailDistribution) {
		return null;
	}

	const destinationStores = Array.from(
		new Set(systemSettings?.crossStoreEmailDistribution?.flatMap((dist) => dist.accountMappings.map((m) => m.destinationStore)))
	);

	const uniqueDestinationStores = uniqBy(destinationStores, (store) => store.storeId);

	return uniqueDestinationStores
		.map((store) => ({
			label: store.storeName,
			value: store.storeId,
		}))
		.sort((storeA, storeB) => storeA.label.localeCompare(storeB.label));
}

function calculateCommissionDifference(commission: IEmployeeCommission) {
	const { frontCommission = 0, backCommission = 0, frontCommissionExport = 0, backCommissionExport = 0 } = commission;

	const commissionArray = [
		{ total: frontCommission, value: frontCommissionExport },
		{ total: backCommission, value: backCommissionExport },
	];

	const diff = commissionArray.reduce((acc, { total, value }) => {
		if (value === 0) {
			return acc;
		}

		return acc + (total - value);
	}, 0);

	return round(diff, 2);
}

function calculateCommissionAmount(commission: IEmployeeCommission) {
	const { frontCommissionExport = 0, backCommissionExport = 0 } = commission;

	return round(frontCommissionExport + backCommissionExport, 2);
}

function getExportCommissionInContext(
	filteredCrossStoreReconciliation: ISalesPerformanceExportInput[],
	salePerformanceId: string,
	commissions: ICommissionDetail[]
): ISalesPerformanceExportInput {
	const item = filteredCrossStoreReconciliation.find((item) => item._id === salePerformanceId);

	if (item) {
		const { _id, commissions } = item;
		return {
			_id,
			commissions: commissions.map(({ locationId, userId, position, frontCommission, backCommission }) => ({
				locationId,
				userId,
				position,
				frontCommission,
				backCommission,
			})),
		};
	}

	return {
		_id: salePerformanceId,
		commissions: commissions.map(({ locationId, userId, position }) => ({
			locationId,
			userId,
			position,
			frontCommission: 0,
			backCommission: 0,
		})),
	};
}

function getCommissionId(salePerformanceId: string, commission: IEmployeeCommission): string {
	return `${salePerformanceId}-${commission.userId}-${commission.position.replaceAll(" ", "-")}`;
}

function getExportedCommissions(filteredCrossStoreReconciliation: ISalesPerformanceExportInput[], salePerformanceId: string) {
	const commissions = filteredCrossStoreReconciliation.find((item) => item._id === salePerformanceId)?.commissions ?? [];

	if (!commissions?.length) {
		return [];
	}

	return commissions.reduce<string[]>((acc, item) => {
		if (item.frontCommission !== 0) {
			acc.push(`${getCommissionId(salePerformanceId, item as IEmployeeCommission)}-frontCommission`);
		}

		if (item.backCommission !== 0) {
			acc.push(`${getCommissionId(salePerformanceId, item as IEmployeeCommission)}-backCommission`);
		}

		return acc;
	}, []);
}

function getUpdatedCommissionForIndividualCheckbox(
	commissions: Pick<IEmployeeCommission, "locationId" | "userId" | "position" | "frontCommission" | "backCommission">[],
	params: { salePerformanceId: string; param: keyof ICommissionDetail; clickedCommissionId: string; commissionValue: number }
) {
	const { salePerformanceId, param, clickedCommissionId, commissionValue } = params;

	return commissions.map((commission) => {
		if (`${getCommissionId(salePerformanceId, commission as IEmployeeCommission)}-${param}` === clickedCommissionId) {
			return { ...commission, [param]: commissionValue };
		}
		return commission;
	});
}

function getUpdatedCommissionsForEntireColumn(
	updatedItemCommissions: Pick<IEmployeeCommission, TPossibleUpdatedItemCommissions>[],
	commissions: ICommissionDetail[],
	params: { salePerformanceId: string; param: keyof ICommissionDetail; checked: boolean; selectableCommissions: ICommissionDetail[] }
) {
	const { salePerformanceId, param, checked, selectableCommissions } = params;

	return updatedItemCommissions.map((commission) => {
		if (!checked) {
			return { ...commission, [param]: 0 };
		}

		const commissionInData = commissions.find((c) => c._id === getCommissionId(salePerformanceId, commission as IEmployeeCommission));
		if (commissionInData && selectableCommissions.some((sc) => sc._id === commissionInData._id)) {
			const value = (commissionInData[param] as number) - (commissionInData[`${param}Export` as keyof ICommissionDetail] as number);
			return { ...commission, [param]: round(value, 2) };
		}
		return commission;
	});
}

function getUpdatedCommissionsForEntireRow(
	updatedItemCommissions: Pick<IEmployeeCommission, TPossibleUpdatedItemCommissions>[],
	params: { salePerformanceId: string; newSelection: GridRowSelectionModel; commissions: ICommissionDetail[] }
) {
	const { salePerformanceId, newSelection, commissions } = params;

	return updatedItemCommissions.map((commission) => {
		const id = getCommissionId(salePerformanceId, commission as IEmployeeCommission);
		if (newSelection.includes(id)) {
			const {
				frontCommission = 0,
				backCommission = 0,
				backCommissionExport = 0,
				frontCommissionExport = 0,
			} = commissions.find((c) => c._id === id) ?? {};
			const frontValue = frontCommission - frontCommissionExport;
			const backValue = backCommission - backCommissionExport;

			return {
				...commission,
				frontCommission: round(frontValue, 2),
				backCommission: round(backValue, 2),
			};
		} else {
			const correspondingCommission = commissions.find((c) => c._id === id);
			if (correspondingCommission) {
				const expectedFrontValue = round(
					(correspondingCommission.frontCommission || 0) - (correspondingCommission.frontCommissionExport || 0),
					2
				);
				const expectedBackValue = round(
					(correspondingCommission.backCommission || 0) - (correspondingCommission.backCommissionExport || 0),
					2
				);

				if (commission.frontCommission === expectedFrontValue && commission.backCommission === expectedBackValue) {
					return { ...commission, frontCommission: 0, backCommission: 0 };
				}
			}

			return commission;
		}
	});
}

function getNewFilteredCrossStoreReconciliation(
	filteredCrossStoreReconciliation: ISalesPerformanceExportInput[],
	salePerformanceId: string,
	updatedCommissions: Pick<IEmployeeCommission, "locationId" | "userId" | "position" | "frontCommission" | "backCommission">[]
): ISalesPerformanceExportInput[] {
	const newFilteredCrossStoreReconciliation: ISalesPerformanceExportInput[] = [];

	let found = false;
	for (const item of filteredCrossStoreReconciliation) {
		if (item._id === salePerformanceId) {
			newFilteredCrossStoreReconciliation.push({ ...item, commissions: updatedCommissions });
			found = true;
		} else {
			newFilteredCrossStoreReconciliation.push(item);
		}
	}

	if (!found) {
		newFilteredCrossStoreReconciliation.push({
			_id: salePerformanceId,
			commissions: updatedCommissions,
		});
	}

	return newFilteredCrossStoreReconciliation;
}

function getCommissionValueByParam(commissions: ICommissionDetail[], id: string, param: keyof ICommissionDetail): number {
	const commission = commissions.find((commission) => commission._id === id);

	if (!commission) {
		return 0;
	}

	const value = ((commission?.[param] as number) ?? 0) - ((commission?.[`${param}Export` as keyof ICommissionDetail] as number) ?? 0);
	return round(value, 2);
}

function getSelectableCommissionsByParam(commissions: ICommissionDetail[], param: keyof ICommissionDetail): ICommissionDetail[] {
	return commissions.filter((commission) => {
		const disabled =
			commission?.[param] &&
			commission?.[`${param}Export` as keyof ICommissionDetail] &&
			commission[param] === commission[`${param}Export` as keyof ICommissionDetail];
		return !disabled;
	});
}

export {
	getIdentifiers,
	getGroupInclusions,
	calculateCommissionDifference,
	calculateCommissionAmount,
	getExportCommissionInContext,
	getCommissionId,
	getExportedCommissions,
	getNewFilteredCrossStoreReconciliation,
	getUpdatedCommissionForIndividualCheckbox,
	getCommissionValueByParam,
	getSelectableCommissionsByParam,
	getUpdatedCommissionsForEntireColumn,
	getUpdatedCommissionsForEntireRow,
};
